import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import TabGridAndFormLayout from "../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../Components/ProGrid/v2";
import DeleteCell from "../../Components/ProGrid/components/DeleteCell";
import ProSelectField from "../../Components/Inputs/SelectField";
import ProTextInput from "../../Components/Inputs/TextField";
import { STAUTES } from "../../../utils/constant";
import usePageState from "../../../utils/customHooks/usePageState";
import {
  GET_ALL_EMPLOYEE_DOCUMENTS,
  DELETE_EMPLOYEE_DOCUMENT,
  GET_ALL_EMPLOYEE_DOCUMENT_TYPE,
  ADD_EMPLOYEE_DOCUMENT,
  UPDATE_EMPLOYEE_DOCUMENT,
  GET_EMPLOYEE_DOCUMENTS_BY_ID
} from "../../../utils/services/apiPath";
import { postApi, deleteApi, getApi, putApi } from "../../../utils/services";
import {
  prepareDefaultValues,
  prepareInitialConfig,
  fieldTruePermission,
  convertFileToBase64Model,
  changeModeForField,
  changeMode
} from "../../../utils/formHelper";
import useColumn from "../../../utils/customHooks/useColumns";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { errorToast, successToast } from "../../../utils/toastHelper";
import { defaultDropdownConfig } from "../../../utils/dropdownHelper";
import FileUploadField from "../../Components/Inputs/FileUploadField";
import NameCell from "../../Components/ProGrid/components/NameCell";
import { useSelector } from "react-redux";
import { loginSelector } from "../../store/features/auth/authSlice";
import Layout from "../../Components/Layout";
import { createODataQuery } from "../../../utils/services/oData";

const EmployeeDocumentsFormConfing = {
  documentName: {
    label: "Document Name",
    perKey: "documentName",
    rules: {
      required: "Document Name is required!"
    }
  },
  employeeDocumentTypeId: {
    label: "Employee Document Type",
    perKey: "employeeDocumentTypeId",
    rules: {
      required: "Document Type is required!"
    }
  },
  documentMediaModel: {
    label: "Document Upload",
    perKey: "documentMediaModel",
    rules: {
      required: "Document is required!"
    }
  }
};

export default function DocumentList() {
  let { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(EmployeeDocumentsFormConfing));
  const [employeeDocumentData, setEmployeeDocumentData] = useState({});
  const [actionData, setActionData] = useState({});
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useSelector(loginSelector);
  const {
    currentPage,
    pageSize,
    data: documentData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const columns = useColumn([
    {
      field: "documentName",
      headerName: "Document Name",
      renderCell: params => <NameCell params={params} onClick={onDocumentClick} />
    },
    {
      field: "employeeDocumentType",
      headerName: "Document Type"
    },
    {
      field: "employeeDocumentId",
      headerName: "Actions",
      width: 160,
      renderCell: params => (
        <>
          <Tooltip title={"Download"} arrow>
            <IconButton onClick={() => handleDownload(params)} color="primary">
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <DeleteCell params={params} onDelete={onDelete} />
        </>
      )
    }
  ]);

  const { handleSubmit, control, setValue } = useForm({ defaultValues: prepareDefaultValues(EmployeeDocumentsFormConfing) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const onEdit = () => {
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "edit" } }), {}));
  };

  const handleReadValueclick = name => {
    setFormConfig(prev => ({ ...prev, [name]: { ...prev[name], mode: "edit" } }));
  };

  const resetForm = () => {
    setActionData({});
    setShowGrid(true);
    setFormConfig(changeMode());
  };

  const onCancel = () => {
    if (actionData?.employeeDocumentId) {
      setFormConfig(changeMode("read"));
    } else {
      resetForm();
    }
  };

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
    setIsAddMode(true);
  };

  const onDocumentClick = params => {
    setShowGrid(false);
    setActionData({ ...params.row, documentMediaModel: params.row.documentMediaModel.path });
    getDocumentData(params.row);
  };

  const getAllDocuments = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ["employeeDocumentType", "documentName"];
    const query = createODataQuery(searchableFields, [], "", searchString, sortModel, pageSize, currentPage);

    const endpoint = `${GET_ALL_EMPLOYEE_DOCUMENTS.replace("{employeeId}", user.employeeId)}${query}`;

    const response = await getApi(endpoint);

    setStatus(STAUTES.IDLE);
    if (response?.error) return errorToast(response.error);
    const rows = response?.value || [];
    const totalRecords = response?.["@odata.count"] ?? rows.length;
    setPageState(prevPageInfo => ({
      ...prevPageInfo,
      data: rows.map(row => ({ ...row, id: row.employeeDocumentId })),
      rowCount: totalRecords
    }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const getAllDocumentTypes = async () => {
    const response = await getApi(`${GET_ALL_EMPLOYEE_DOCUMENT_TYPE}?models=EMPLOYEEDOCUMENTTYPE`);

    const employeeDocumentType = response.find(item => item.typeName === "EMPLOYEEDOCUMENTTYPE");

    if (employeeDocumentType) {
      setDocumentTypes(
        employeeDocumentType.typeDropDowns.map(item => ({
          label: item.label,
          value: item.value
        }))
      );
    } else {
      setDocumentTypes([]);
    }
  };

  const getDocumentData = async updateData => {
    const data = {
      documentName: updateData?.documentName,
      employeeDocumentTypeId: updateData?.employeeDocumentTypeId,
      documentMediaModel: updateData?.documentMediaModel?.path
    };
    setEmployeeDocumentData(data);
    Object.keys(formConfig).forEach(field => setValue(field, field === "documentMediaModel" ? null : data[field]));
  };

  const handleDownload = params => {
    const file = params.row.documentMediaModel?.path;
    const link = document.createElement("a");
    link.href = file;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const onDelete = async id => {
    const deleteEndPoint = `${DELETE_EMPLOYEE_DOCUMENT.replace("{employeeId}", user.employeeId)}/${id}`;
    const response = await deleteApi(deleteEndPoint);
    if (response?.error) return errorToast(response.error);
    getAllDocuments();
    successToast("Employee document deleted successfully.");
  };

  const onSave = async data => {
    if (isSaving) return;
    setIsSaving(true);
    setActionData({});
    const base64ProfilePicture = await convertFileToBase64Model(data.documentMediaModel);
    const payload = {
      employeeDocumentTypeId: data?.employeeDocumentTypeId,
      employeeId: user.employeeId,
      documentName: data?.documentName,
      documentMediaModel: base64ProfilePicture || null
      // employeeDocumentId: actionData?.employeeDocumentId
    };
    const addEndPoint = `${ADD_EMPLOYEE_DOCUMENT.replace("{employeeId}", user.employeeId)}`;
    const updateEndPoint = `${UPDATE_EMPLOYEE_DOCUMENT.replace("{employeeId}", user.employeeId)}${actionData?.employeeDocumentId}`;
    const response = actionData?.employeeDocumentId ? await putApi(updateEndPoint, payload) : await postApi(addEndPoint, payload);
    setIsSaving(false);
    if (response?.error) return errorToast(response.error);
    setShowGrid(true);
    setActionData({});
    getAllDocuments();
    successToast(`Employee Document ${actionData?.employeeDocumentId ? "Updated" : "Added"} Successfully`);
    setFormConfig(changeMode("read"));
  };

  const getDocumentById = async id => {
    const getByIdEndPoint = `${GET_EMPLOYEE_DOCUMENTS_BY_ID.replace("{employeeId}", user.employeeId)}/${id}`;
    const response = await getApi(getByIdEndPoint);
    if (response.error) return errorToast(response.error);
    const data = {
      documentName: response?.documentName,
      employeeDocumentTypeId: response?.employeeDocumentTypeId,
      documentMediaModel: response?.documentMediaModel?.path
    };
    setActionData(response);
    setIsAddMode(false);
    setEmployeeDocumentData(data);
    Object.keys(formConfig).forEach(field => setValue(field, field === "documentMediaModel" ? null : data[field]));
  };

  useEffect(() => {
    getAllDocuments();
    getAllDocumentTypes();
    if (isAddMode) onEdit();
  }, [getAllDocuments]);

  const defaultFormProps = { control, formValues: employeeDocumentData, isAddMode, handleReadValueclick };

  return (
    <Layout>
      <TabGridAndFormLayout
        showGrid={showGrid}
        title={"Document"}
        backAction={() => resetForm()}
        backLabel="Go back to Documents List"
        beingEdited={isEditModeOn}
        onEdit={onEdit}
        canView={true}
        hideEdit={true}
        onSave={handleSubmit(onSave)}
        onCancel={onCancel}
        isSaving={isSaving}
      >
        {showGrid && (
          <ProGrid
            title="Documents"
            loading={status === STAUTES.LOADING}
            options={{
              getRowId: row => row.employeeDocumentId,
              rowCount: rowCount,
              paginationMode: "server",
              paginationModel: { pageSize: pageSize, page: currentPage },
              onPaginationModelChange: setPagination,
              sortingMode: "server",
              onSortModelChange: setSortModel
            }}
            columns={columns}
            addButtonLabel="+ Add Document"
            rows={documentData}
            searchMode={!!searchString}
            searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
            addButtonAction={addButtonAction}
          />
        )}
        {!showGrid && (
          <>
            <ProTextInput size={4} {...defaultFormProps} {...formConfig.documentName} permission={fieldTruePermission} />
            <ProSelectField {...defaultFormProps} {...formConfig.employeeDocumentTypeId} options={documentTypes} permission={fieldTruePermission} />
            <FileUploadField
              {...defaultFormProps}
              {...formConfig.documentMediaModel}
              inputProps={{ accept: "file/*" }}
              permission={fieldTruePermission}
            />
          </>
        )}
      </TabGridAndFormLayout>
    </Layout>
  );
}
